import { useEffect, useState } from "react";
import {
  chatFieldsLabels,
  requiredFieldsStatuses,
  type IOption,
} from "../../App.interface";
import type {
  ChatFormFields,
  IChatFormData,
  IChatFormTabProps,
} from "../../pages/first-line/ChatForm.interface";
import { InfoField } from "../form-fields/InfoField";
import DateField from "../form-fields/DateField";
import DateTimeField from "../form-fields/DateTimeField";
import SelectField from "../form-fields/SelectField";
import SwitchField from "../form-fields/SwitchField";
import TextField from "../form-fields/TextField";
import InfoPopup from "../InfoPopup";
import api from "../../api/axios";

const ChatFormTab = ({
  phone,
  leadId,
  contactId,
  incomingFieldValues,
}: IChatFormTabProps) => {
  const [showPopup, setShowPopup] = useState<boolean>(false);

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [message, setMessage] = useState<string>("");

  const [formData, setFormData] = useState<IChatFormData>({
    leadId: null,
    contactId: null,
    newStatusId: null,
    fields: {
      stage: "",
      phone: phone ?? "",
      age: "",
      citizenship: null,
      city: null,
      ka: null,
      lastName: "",
      firstName: "",
      middleName: "",
      dateOfBirth: "",
      education: null,
      contractType: null,
      role: null,
      contractDate: "",
      educationDateTime: "",
      educationCenter: null,
      inn: "",
      insuranceNumber: "",
      transportType: null,
      foreignCitizen: false,
      minor: false,
      office: false,
      preFilledFCForm: false,
      rejectionReason: null,
      reservationReason: null,
      company: null,
      region: null,
      hireType: null,
      sex: null,
    },
  });

  const [requiredFieldsConfig, setRequiredFieldsConfig] = useState<
    Record<string, boolean>
  >({});

  const [requiredFieldsStatus, setRequiredFieldsStatus] = useState<
    (typeof requiredFieldsStatuses)[keyof typeof requiredFieldsStatuses]
  >(requiredFieldsStatuses.loading);

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      leadId: leadId ?? prev.leadId,
      contactId: contactId ?? prev.contactId,
      fields: {
        ...prev.fields,
        stage: incomingFieldValues?.stage ?? prev.fields.stage,
        age: incomingFieldValues?.age ?? prev.fields.age,
        citizenship:
          incomingFieldValues?.citizenship ?? prev.fields.citizenship,
        city: incomingFieldValues?.city ?? prev.fields.city,
        ka: incomingFieldValues?.ka ?? prev.fields.ka,
        lastName: incomingFieldValues?.lastName ?? prev.fields.lastName,
        firstName: incomingFieldValues?.firstName ?? prev.fields.firstName,
        middleName: incomingFieldValues?.middleName ?? prev.fields.middleName,
        dateOfBirth:
          incomingFieldValues?.dateOfBirth ?? prev.fields.dateOfBirth,
        education: incomingFieldValues?.education ?? prev.fields.education,
        contractType:
          incomingFieldValues?.contractType ?? prev.fields.contractType,
        role: incomingFieldValues?.role ?? prev.fields.role,
        contractDate:
          incomingFieldValues?.contractDate ?? prev.fields.contractDate,
        educationDateTime:
          incomingFieldValues?.educationDateTime ??
          prev.fields.educationDateTime,
        educationCenter:
          incomingFieldValues?.educationCenter ?? prev.fields.educationCenter,
        inn: incomingFieldValues?.inn ?? prev.fields.inn,
        insuranceNumber:
          incomingFieldValues?.insuranceNumber ?? prev.fields.insuranceNumber,
        transportType:
          incomingFieldValues?.transportType ?? prev.fields.transportType,
        foreignCitizen:
          incomingFieldValues?.foreignCitizen ?? prev.fields.foreignCitizen,
        minor: incomingFieldValues?.minor ?? prev.fields.minor,
        office: incomingFieldValues?.office ?? prev.fields.office,
        preFilledFCForm:
          incomingFieldValues?.preFilledFCForm ?? prev.fields.preFilledFCForm,
        rejectionReason:
          incomingFieldValues?.rejectionReason ?? prev.fields.rejectionReason,
        reservationReason:
          incomingFieldValues?.reservationReason ??
          prev.fields.reservationReason,
        company: incomingFieldValues?.company ?? prev.fields.company,
        region: incomingFieldValues?.region ?? prev.fields.region,
        hireType: incomingFieldValues?.hireType ?? prev.fields.hireType,
        sex: incomingFieldValues?.sex ?? prev.fields.sex,
      },
    }));
  }, [incomingFieldValues]);

  useEffect(() => {
    fetchRequiredFields();
  }, []);

  const FALLBACK_REQUIRED_FIELDS: ChatFormFields[] = [
    "age",
    "citizenship",
    "lastName",
    "firstName",
    "education",
    "contractType",
    "role",
    "contractDate",
    "educationDateTime",
    "educationCenter",
    "company",
    "region",
    "sex",
  ];

  const requiredFields =
    requiredFieldsStatus === requiredFieldsStatuses.failed
      ? FALLBACK_REQUIRED_FIELDS
      : Object.entries(requiredFieldsConfig)
          .filter(([, isRequired]) => isRequired)
          .map(([field]) => field as ChatFormFields);

  const RESERVE_REASON_NAME = "Резерв";

  const isReserveSelected =
    formData.fields.rejectionReason?.value === RESERVE_REASON_NAME;

  const [invalid, setInvalid] = useState<Record<string, boolean>>({});

  const handleChange = (
    field: ChatFormFields,
    value: string | boolean | IOption | null | IOption[],
  ) => {
    setFormData((prev) => {
      const nextFields = { ...prev.fields, [field]: value };

      if (
        field === "rejectionReason" &&
        (value as IOption | null)?.value !== RESERVE_REASON_NAME
      ) {
        nextFields.reservationReason = null;
      }

      return { ...prev, fields: nextFields };
    });

    setInvalid((prev) => ({ ...prev, [field]: false }));
  };

  const handleSubmit = async () => {
    if (isSubmitting || !validate()) return;

    setIsSubmitting(true);

    if (
      formData.fields.rejectionReason?.id ||
      formData.fields.reservationReason?.id
    ) {
      formData.newStatusId = 143;
    }

    const hasPhone = formData.fields.phone.trim() !== "";

    const [sendFormDataResult, updateContactPhoneResult] = await Promise.all([
      sendFormData(),
      hasPhone ? updateContactPhone() : true,
    ]);

    const errors = [];

    if (!sendFormDataResult) {
      errors.push("Не удалось отправить данные формы");
    }

    if (!updateContactPhoneResult) {
      errors.push("Не удалось обновить телефон контакта");
    }

    if (errors.length > 0) {
      setMessage(errors.join("\n"));
      setShowPopup(true);
      return;
    }

    setMessage("Данные успешно отправлены");
    setShowPopup(true);
  };

  const sendFormData = async () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { ka, ...fieldsToSend } = formData.fields;

    const body = {
      ...formData,
      fields: fieldsToSend,
    };

    try {
      await api.patch("/api-gateway/lead", body);
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const updateContactPhone = async () => {
    try {
      await api.patch("/api-gateway/contact", {
        contactId: formData.contactId,
        phone: formData.fields.phone,
      });
      return true;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  const validate = () => {
    if (isReserveSelected && !formData.fields.reservationReason) {
      setInvalid({ reservationReason: true });
      return false;
    }

    if (formData.fields.rejectionReason || formData.fields.reservationReason) {
      setInvalid({});
      return true;
    }

    const invalidFields: Record<string, boolean> = {};

    requiredFields.forEach((field) => {
      const val = formData.fields[field];

      if (!val || (Array.isArray(val) && val.length === 0)) {
        invalidFields[field] = true;
      }
    });

    setInvalid(invalidFields);
    return Object.keys(invalidFields).length === 0;
  };

  const fetchRequiredFields = async () => {
    try {
      const { data } = await api.get("/api-gateway/form-required-fields", {
        params: { form: "chat" },
      });
      setRequiredFieldsConfig(data.formRequiredFields);
      setRequiredFieldsStatus(requiredFieldsStatuses.loaded);
    } catch (error) {
      console.log("Не удалось получить обязательные поля", error);
      setRequiredFieldsStatus(requiredFieldsStatuses.failed);
    }
  };

  const handlePopupClose = () => {
    setShowPopup(false);
    setIsSubmitting(false);
  };

  return (
    <div className="form">
      <InfoField value={formData.fields.stage}></InfoField>

      <TextField
        label={chatFieldsLabels.phone}
        value={formData.fields.phone}
        onChange={(value) => handleChange("phone", value)}
        invalid={invalid.phone}
      ></TextField>

      <TextField
        label={chatFieldsLabels.age}
        value={formData.fields.age}
        onChange={(value) => handleChange("age", value)}
        invalid={invalid.age}
      ></TextField>

      <SelectField
        label={chatFieldsLabels.citizenship}
        field="citizenship"
        value={formData.fields.citizenship}
        onChange={(value) => handleChange("citizenship", value)}
        invalid={invalid.citizenship}
      ></SelectField>

      <SelectField
        label={chatFieldsLabels.region}
        field="region"
        value={formData.fields.region}
        onChange={(value) => handleChange("region", value)}
        invalid={invalid.region}
      ></SelectField>

      <SelectField
        label={chatFieldsLabels.city}
        field="city"
        value={formData.fields.city}
        onChange={(value) => handleChange("city", value)}
        invalid={invalid.city}
      ></SelectField>

      <TextField
        label={chatFieldsLabels.ka}
        value={formData.fields.ka?.value ?? ""}
        onChange={(value) => handleChange("ka", value)}
        readOnly={true}
      ></TextField>

      <TextField
        label={chatFieldsLabels.lastName}
        value={formData.fields.lastName}
        onChange={(value) => handleChange("lastName", value)}
        invalid={invalid.lastName}
      ></TextField>

      <TextField
        label={chatFieldsLabels.firstName}
        value={formData.fields.firstName}
        onChange={(value) => handleChange("firstName", value)}
        invalid={invalid.firstName}
      ></TextField>

      <TextField
        label={chatFieldsLabels.middleName}
        value={formData.fields.middleName}
        onChange={(value) => handleChange("middleName", value)}
        invalid={invalid.middleName}
      ></TextField>

      <SelectField
        label={chatFieldsLabels.sex}
        field="sex"
        value={formData.fields.sex}
        onChange={(value) => handleChange("sex", value)}
        invalid={invalid.sex}
      ></SelectField>

      <DateField
        label={chatFieldsLabels.dateOfBirth}
        value={formData.fields.dateOfBirth}
        onChange={(value) => handleChange("dateOfBirth", value)}
        invalid={invalid.dateOfBirth}
      ></DateField>

      <SelectField
        label={chatFieldsLabels.education}
        field="education"
        value={formData.fields.education}
        onChange={(value) => handleChange("education", value)}
        invalid={invalid.education}
      ></SelectField>

      <SelectField
        label={chatFieldsLabels.contractType}
        field="contractType"
        value={formData.fields.contractType}
        onChange={(value) => handleChange("contractType", value)}
        invalid={invalid.contractType}
      ></SelectField>

      <SelectField
        label={chatFieldsLabels.role}
        field="role"
        value={formData.fields.role}
        onChange={(value) => handleChange("role", value)}
        invalid={invalid.role}
      ></SelectField>

      <SelectField
        label={chatFieldsLabels.hireType}
        field="hireType"
        value={formData.fields.hireType}
        onChange={(value) => handleChange("hireType", value)}
        invalid={invalid.hireType}
      ></SelectField>

      <DateField
        label={chatFieldsLabels.contractDate}
        value={formData.fields.contractDate}
        onChange={(value) => handleChange("contractDate", value)}
        invalid={invalid.contractDate}
      ></DateField>

      <DateTimeField
        label={chatFieldsLabels.educationDateTime}
        value={formData.fields.educationDateTime}
        onChange={(value) => handleChange("educationDateTime", value)}
        invalid={invalid.educationDateTime}
      ></DateTimeField>

      <SelectField
        label={chatFieldsLabels.educationCenter}
        field="educationCenter"
        value={formData.fields.educationCenter}
        onChange={(value) => handleChange("educationCenter", value)}
        invalid={invalid.educationCenter}
      ></SelectField>

      <TextField
        label={chatFieldsLabels.inn}
        value={formData.fields.inn}
        onChange={(value) => handleChange("inn", value)}
        invalid={invalid.inn}
      ></TextField>

      <TextField
        label={chatFieldsLabels.insuranceNumber}
        value={formData.fields.insuranceNumber}
        onChange={(value) => handleChange("insuranceNumber", value)}
        invalid={invalid.insuranceNumber}
      ></TextField>

      <SelectField
        label={chatFieldsLabels.transportType}
        field="transportType"
        value={formData.fields.transportType}
        onChange={(value) => handleChange("transportType", value)}
        invalid={invalid.transportType}
      ></SelectField>

      <SwitchField
        label={chatFieldsLabels.foreignCitizen}
        value={formData.fields.foreignCitizen}
        onChange={(value) => handleChange("foreignCitizen", value)}
      ></SwitchField>

      <SwitchField
        label={chatFieldsLabels.minor}
        value={formData.fields.minor}
        onChange={(value) => handleChange("minor", value)}
      ></SwitchField>

      <SwitchField
        label={chatFieldsLabels.office}
        value={formData.fields.office}
        onChange={(value) => handleChange("office", value)}
      ></SwitchField>

      <SwitchField
        label={chatFieldsLabels.preFilledFCForm}
        value={formData.fields.preFilledFCForm}
        onChange={(value) => handleChange("preFilledFCForm", value)}
      ></SwitchField>

      <SelectField
        label={chatFieldsLabels.company}
        field="company"
        value={formData.fields.company}
        onChange={(value) => handleChange("company", value)}
        invalid={invalid.company}
      ></SelectField>

      <SelectField
        label={chatFieldsLabels.rejectionReason}
        field="rejectionReason"
        value={formData.fields.rejectionReason}
        onChange={(value) => handleChange("rejectionReason", value)}
        invalid={invalid.rejectionReason}
        clearable={true}
      ></SelectField>

      {isReserveSelected && (
        <SelectField
          label={chatFieldsLabels.reservationReason}
          field="reservationReason"
          value={formData.fields.reservationReason}
          onChange={(value) => handleChange("reservationReason", value)}
          invalid={invalid.reservationReason}
          clearable={true}
        ></SelectField>
      )}

      <button
        className="submit-button"
        onClick={handleSubmit}
        disabled={
          isSubmitting ||
          requiredFieldsStatus === requiredFieldsStatuses.loading
        }
      >
        {isSubmitting ? (
          <>
            Отправка данных анкеты
            <span className="loading-dots" aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </>
        ) : requiredFieldsStatus !== requiredFieldsStatuses.loading ? (
          "Отправить"
        ) : (
          <>
            Загрузка
            <span className="loading-dots" aria-hidden="true">
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </>
        )}
      </button>

      {showPopup && (
        <InfoPopup message={message} onClose={handlePopupClose}></InfoPopup>
      )}
    </div>
  );
};

export default ChatFormTab;
